/**
 * YourPlanCard Component - Sprint 24 Phase 2
 * 
 * Displays tenant's current plan with:
 * - Plan name and tier
 * - Seat usage gauge with warnings
 * - Features available/locked
 * - Upgrade CTA
 * 
 * Consumes: GET /api/billing/admin/your-plan/{tenantId}
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  Crown, 
  Check, 
  X, 
  AlertTriangle, 
  AlertCircle, 
  Info,
  ArrowUpRight,
  Zap,
  Shield,
  Database,
  Workflow,
  Bot,
  Link2,
  Gauge,
  RefreshCw,
  Building2
} from 'lucide-react';
import { api } from '../../api/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { useProductMode } from '../../context/ProductModeContext';

// Feature icon mapping
const FEATURE_ICONS = {
  'AI Copilot (Full)': Bot,
  'AI Copilot (Basic)': Bot,
  'Advanced AI Agents': Zap,
  'Jira/ADO Full Sync': Link2,
  'Jira/ADO Standard': Link2,
  'Workflow Builder': Workflow,
  'SSO/MFA': Shield,
  'Power BI': Database,
  'Advanced Analytics': Gauge,
};

// Plan tier colors
const TIER_COLORS = {
  'team': 'bg-slate-100 text-slate-800 border-slate-300',
  'business': 'bg-blue-100 text-blue-800 border-blue-300',
  'enterprise': 'bg-purple-100 text-purple-800 border-purple-300',
  'enterprise_plus': 'bg-amber-100 text-amber-800 border-amber-300',
  'unlimited_enterprise': 'bg-emerald-100 text-emerald-800 border-emerald-300',
};

const YourPlanCard = ({ tenantId, onUpgradeClick, compact = false }) => {
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // RBAC FIX: Check if user is admin (sysadmin/platform admin should not see commercial upsells)
  const { isAdminBypass } = useProductMode();

  const loadPlanData = useCallback(async () => {
    if (!tenantId || tenantId === 'default-tenant') {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/billing/admin/your-plan/${tenantId}`);
      setPlanData(response.data);
    } catch (err) {
      console.error('Failed to load plan data:', err);
      setError('Failed to load plan information');
      toast.error('Failed to load plan information');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadPlanData();
  }, [loadPlanData]);

  if (loading) {
    return (
      <Card className={compact ? 'w-full' : 'w-full max-w-md'}>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-32">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !planData) {
    return (
      <Card className={compact ? 'w-full' : 'w-full max-w-md'}>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-32 text-gray-500">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error || 'No plan data available'}
          </div>
        </CardContent>
      </Card>
    );
  }

  const { plan, seats, warning, features, upgrade } = planData;
  const seatPercent = seats?.usage_percent || 0;
  
  // Determine seat gauge color
  const getSeatGaugeColor = () => {
    if (seatPercent >= 100) return 'bg-red-500';
    if (seatPercent >= 95) return 'bg-amber-500';
    if (seatPercent >= 80) return 'bg-yellow-500';
    return 'bg-emerald-500';
  };

  // Determine warning icon
  const getWarningIcon = () => {
    if (!warning) return null;
    switch (warning.level) {
      case 'critical': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'warning': return <AlertCircle className="w-4 h-4 text-amber-500" />;
      case 'info': return <Info className="w-4 h-4 text-blue-500" />;
      default: return null;
    }
  };

  if (compact) {
    // Compact version for sidebars/headers
    // RBAC FIX: Admins see platform status, not commercial upgrade messaging
    if (isAdminBypass) {
      return (
        <div className="p-4 bg-gradient-to-r from-slate-700 to-slate-800 rounded-lg border border-slate-600">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-emerald-400" />
              <span className="font-medium text-sm text-white">Platform Admin</span>
            </div>
            <Badge variant="outline" className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
              FULL ACCESS
            </Badge>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <Shield className="w-3 h-3" />
            <span>Tenant: {plan?.name || 'Enterprise'}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
            <Users className="w-3 h-3" />
            <span>{seats?.current}/{seats?.is_unlimited ? '∞' : seats?.limit} users</span>
          </div>
        </div>
      );
    }
    
    return (
      <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Crown className="w-4 h-4 text-amber-500" />
            <span className="font-medium text-sm">{plan?.name || 'Unknown Plan'}</span>
          </div>
          <Badge variant="outline" className={TIER_COLORS[plan?.tier] || ''}>
            {plan?.tier?.replace('_', ' ').toUpperCase()}
          </Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Users className="w-3 h-3" />
          <span>{seats?.current}/{seats?.is_unlimited ? '∞' : seats?.limit} users</span>
          {warning && getWarningIcon()}
        </div>
        {upgrade?.available && (
          <Button 
            size="sm" 
            variant="outline" 
            className="w-full mt-3 text-xs"
            onClick={onUpgradeClick}
          >
            Upgrade Plan <ArrowUpRight className="w-3 h-3 ml-1" />
          </Button>
        )}
      </div>
    );
  }

  // RBAC FIX: Full card version - Admin sees platform status, not commercial content
  if (isAdminBypass) {
    return (
      <Card className="w-full max-w-md bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700" data-testid="your-plan-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-400" />
              <CardTitle className="text-lg text-white">Platform Status</CardTitle>
            </div>
            <Badge 
              variant="outline" 
              className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 px-3 py-1"
              data-testid="plan-tier-badge"
            >
              ADMIN ACCESS
            </Badge>
          </div>
          <CardDescription className="text-slate-400">
            Full platform access as System Administrator
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">
          {/* Tenant License Info */}
          <div className="space-y-2" data-testid="license-info-section">
            <h4 className="text-sm font-medium text-slate-300">Tenant License</h4>
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Plan</span>
                <span className="font-medium text-white">{plan?.name || 'Enterprise'}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-slate-400">Tier</span>
                <Badge variant="secondary" className="capitalize bg-slate-600 text-slate-200">
                  {plan?.tier?.replace('_', ' ') || 'Enterprise'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Seat Usage Section - Admins see this for monitoring */}
          <div className="space-y-2" data-testid="seat-usage-section">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-slate-400">
                <Users className="w-4 h-4" />
                User Seats
              </span>
              <span className="font-medium text-white">
                {seats?.current} / {seats?.is_unlimited ? '∞' : seats?.limit}
              </span>
            </div>
            
            {!seats?.is_unlimited && (
              <div className="space-y-1">
                <Progress 
                  value={Math.min(seatPercent, 100)} 
                  className="h-2 bg-slate-700"
                  indicatorClassName={getSeatGaugeColor()}
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>{seatPercent.toFixed(1)}% utilized</span>
                  {seats?.limit - seats?.current > 0 && (
                    <span>{seats?.limit - seats?.current} available</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Support Level */}
          <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg text-sm">
            <span className="text-slate-400">Support Level</span>
            <Badge variant="secondary" className="capitalize bg-emerald-500/20 text-emerald-300">
              {plan?.support_level?.replace('_', ' ') || 'Premium'}
            </Badge>
          </div>

          {/* Admin Actions - No commercial upsells */}
          <div className="pt-2">
            <Button 
              variant="outline"
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
              onClick={() => window.location.href = '/platform/tenants'}
            >
              <Building2 className="w-4 h-4 mr-2" />
              Manage Tenants
            </Button>
          </div>

          {/* Billing Info Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-slate-700 text-xs text-slate-500">
            <span>Currency: {planData.billing?.currency || 'USD'}</span>
            <span>Renewal: Day {planData.billing?.renewal_day || 1}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full card version for regular users
  return (
    <Card className="w-full max-w-md" data-testid="your-plan-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-500" />
            <CardTitle className="text-lg">Your Plan</CardTitle>
          </div>
          <Badge 
            variant="outline" 
            className={`${TIER_COLORS[plan?.tier] || ''} px-3 py-1`}
            data-testid="plan-tier-badge"
          >
            {plan?.name || plan?.tier?.replace('_', ' ').toUpperCase()}
          </Badge>
        </div>
        {plan?.is_deprecated && (
          <CardDescription className="text-amber-600 flex items-center gap-1 mt-1">
            <AlertCircle className="w-3 h-3" />
            Legacy plan - Consider upgrading to a new tier
          </CardDescription>
        )}
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Seat Usage Section */}
        <div className="space-y-2" data-testid="seat-usage-section">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-gray-600">
              <Users className="w-4 h-4" />
              User Seats
            </span>
            <span className="font-medium">
              {seats?.current} / {seats?.is_unlimited ? '∞' : seats?.limit}
            </span>
          </div>
          
          {!seats?.is_unlimited && (
            <div className="space-y-1">
              <Progress 
                value={Math.min(seatPercent, 100)} 
                className="h-2"
                indicatorClassName={getSeatGaugeColor()}
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>{seatPercent.toFixed(1)}% used</span>
                {seats?.limit - seats?.current > 0 && (
                  <span>{seats?.limit - seats?.current} available</span>
                )}
              </div>
            </div>
          )}
          
          {/* Warning Banner */}
          {warning && (
            <div 
              className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                warning.level === 'critical' ? 'bg-red-50 text-red-800 border border-red-200' :
                warning.level === 'warning' ? 'bg-amber-50 text-amber-800 border border-amber-200' :
                'bg-blue-50 text-blue-800 border border-blue-200'
              }`}
              data-testid="seat-warning-banner"
            >
              {getWarningIcon()}
              <span>{warning.message}</span>
            </div>
          )}
        </div>

        {/* Features Section */}
        <div className="space-y-2" data-testid="features-section">
          <h4 className="text-sm font-medium text-gray-700">Key Features</h4>
          <div className="grid grid-cols-1 gap-2">
            {features?.slice(0, 6).map((feature, idx) => {
              const IconComponent = FEATURE_ICONS[feature.label] || Check;
              return (
                <div 
                  key={idx}
                  className={`flex items-center justify-between p-2 rounded-md text-sm ${
                    feature.enabled 
                      ? 'bg-emerald-50 text-emerald-800' 
                      : 'bg-gray-50 text-gray-500'
                  }`}
                  data-testid={`feature-${feature.key}`}
                >
                  <div className="flex items-center gap-2">
                    <IconComponent className="w-4 h-4" />
                    <span>{feature.label}</span>
                  </div>
                  {feature.enabled ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Badge variant="outline" className="text-xs bg-gray-100">
                      Upgrade
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Support Level */}
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
          <span className="text-gray-600">Support Level</span>
          <Badge variant="secondary" className="capitalize">
            {plan?.support_level?.replace('_', ' ') || 'Standard'}
          </Badge>
        </div>

        {/* Upgrade CTA - Only shown to non-admin users */}
        {upgrade?.available && !isAdminBypass && (
          <div className="space-y-3 pt-2" data-testid="upgrade-section">
            <Button 
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              onClick={onUpgradeClick}
              data-testid="upgrade-plan-btn"
            >
              <ArrowUpRight className="w-4 h-4 mr-2" />
              {upgrade.cta || 'Upgrade Plan'}
            </Button>
            
            {upgrade.options?.length > 0 && (
              <div className="text-xs text-center text-gray-500">
                Upgrade options: {upgrade.options.map(o => o.name).join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Billing Info Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100 text-xs text-gray-500">
          <span>Currency: {planData.billing?.currency || 'USD'}</span>
          <span>Renewal: Day {planData.billing?.renewal_day || 1}</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default YourPlanCard;
